// Display-status for a drip: the label the StatusLabel actually shows.
//
// `deriveDripStatus` returns the drip's LOGICAL status (draft/active/paused/…). But an
// `active` drip whose first send is still in the FUTURE and which has sent NOTHING is
// not really "Running" yet — it reads "Running · 0 sent" days before anything goes out
// (the operator-reported confusion). This wraps deriveDripStatus and downgrades exactly
// that case to `scheduled` (StatusLabel renders it "Scheduled"). Everything else is
// passed through unchanged, so a genuinely-sending or past-due drip still reads Running.
//
// Kept PURE and separate from deriveDripStatus (which several call sites use for LOGIC,
// not just display) so this only affects the label, and the derivation is unit-testable.

import type { Drip, DripStatus } from '@bt-common/schemas/bt/drip';
import type { Campaign, CampaignStatus } from '@common-models/campaign-model';
import { deriveDripStatus } from './drip-status';

export type DripDisplayStatus = DripStatus | 'scheduled';

const SENDING_OR_DONE: ReadonlyArray<CampaignStatus> = [
  'running', 'completed', 'complete-sent', 'complete-delivered',
];

/** A drip has "started sending" when any step campaign has a send on the board —
 *  a positive sentCount, or a status past pending (running / any complete-*). */
export function hasDripStartedSending(stepCampaigns: Campaign[]): boolean {
  return stepCampaigns.some(
    (c) => (c.sentCount ?? 0) > 0 || (c.campaignStatus != null && SENDING_OR_DONE.includes(c.campaignStatus)),
  );
}

const toMs = (v: unknown): number | undefined => {
  if (!v) return undefined;
  const ms = new Date(v as string | number | Date).getTime();
  return Number.isFinite(ms) ? ms : undefined;
};

/**
 * The status to DISPLAY for a drip. `scheduled` only when the drip is `active`, has sent
 * nothing, and its start (step-0 campaign dueDate, else the drip's own schedule dueDate)
 * is still in the future. Otherwise = deriveDripStatus (unchanged).
 */
export function deriveDripDisplayStatus(
  drip: Pick<Drip, 'status' | 'steps' | 'schedule' | 'startedAt'> | undefined,
  stepCampaigns: Campaign[],
  now: Date = new Date(),
): DripDisplayStatus {
  const base = deriveDripStatus(drip, stepCampaigns);
  if (base !== 'active') return base;           // only a "Running"-looking drip can downgrade
  if (hasDripStartedSending(stepCampaigns)) return base; // already sending → keep Running
  const step0 = stepCampaigns.find((c) => c.dripStepIndex === 0);
  const startMs = toMs(step0?.schedule?.dueDate) ?? toMs(drip?.schedule?.dueDate);
  if (startMs !== undefined && startMs > now.getTime()) return 'scheduled';
  return base;                                  // no future start signal → leave as-is
}
